import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Button from './Button';

const Dropdown = ({ label, value, onChange, options, disabled = false, onAddNew, placeholder }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [newValue, setNewValue] = React.useState('');
  const [error, setError] = React.useState('');

  const handleSelectChange = (e) => {
    if (e.target.value === '__ADD_NEW__') {
      setDialogOpen(true);
      setNewValue('');
      setError('');
    } else {
      onChange(e);
    }
  };

  const handleAddNew = () => {
    const trimmedValue = newValue.trim();

    if (!trimmedValue) {
      setError('Please enter a value');
      return;
    }

    if (options.some(opt => (typeof opt === 'object' ? opt.value : opt) === trimmedValue)) {
      setError('This value already exists');
      return;
    }

    if (onAddNew) {
      onAddNew(trimmedValue);
    }

    setDialogOpen(false);
    setNewValue('');
    setError('');
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setNewValue('');
    setError('');
  };

  return (
    <>
      <FormControl fullWidth disabled={disabled} size="small">
        {label && <InputLabel>{label}</InputLabel>}
        <Select 
          value={value} 
          label={label} 
          onChange={handleSelectChange}
          displayEmpty={!!placeholder}
          renderValue={(selected) => {
            if (!selected && placeholder) {
              return <span style={{ color: '#9ca3af' }}>{placeholder}</span>;
            }
            const selectedOption = options.find(opt => (typeof opt === 'object' ? opt.value : opt) === selected);
            return typeof selectedOption === 'object' ? selectedOption.label : selectedOption;
          }}
        >
          {placeholder && (
            <MenuItem disabled value="">
              <em>{placeholder}</em>
            </MenuItem>
          )}
          {options.map((option) => {
            const isObject = typeof option === 'object' && option !== null;
            const optionValue = isObject ? option.value : option;
            const optionLabel = isObject ? option.label : option;
            return (
              <MenuItem key={optionValue} value={optionValue}>
                {optionLabel}
              </MenuItem>
            );
          })}
          {onAddNew && (
            <MenuItem
              value="__ADD_NEW__"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                borderTop: '1px solid',
                borderColor: 'divider',
                mt: 1,
                pt: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon fontSize="small" />
                Add New {label || 'Item'}
              </Box>
            </MenuItem>
          )}
        </Select>
      </FormControl>

      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Add New {label || 'Item'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={`New ${label || 'Item'}`}
            type="text"
            fullWidth
            variant="outlined"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            error={!!error}
            helperText={error}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddNew();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleAddNew} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(Dropdown);
